import React, { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2, ChevronLeft, ChevronRight, ShoppingBag } from "lucide-react";

const fmt = (v) => `R$ ${(v || 0).toFixed(2).replace(".", ",")}`;
const fmtDate = (s) => s ? new Date(s).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "";

const STATUS_COLORS = {
  paid: "bg-emerald-100 text-emerald-800",
  pending: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-800",
  cancelled: "bg-stone-200 text-stone-700",
};

export default function OrdersTable({ scope = "admin" }) {
  // scope: "admin" → /admin/reports/orders ; "merchant" → /merchant/reports/orders
  const [data, setData] = useState({ totals: {}, orders: [], page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ from: "", to: "", status: "all", company_id: "" });
  const [companies, setCompanies] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (scope === "admin") api.get("/companies?include_inactive=true").then((r) => setCompanies(r.data || [])).catch(() => {});
    else api.get("/companies/owner/mine").then((r) => setCompanies(r.data || [])).catch(() => {});
  }, [scope]);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, page_size: 20 });
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
    if (filters.status && filters.status !== "all") params.set("status", filters.status);
    if (filters.company_id && filters.company_id !== "all") params.set("company_id", filters.company_id);
    const url = scope === "admin" ? `/admin/reports/orders?${params}` : `/merchant/reports/orders?${params}`;
    api.get(url).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [scope, page, filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid={`orders-table-${scope}`}>
      <Card className="p-4 rounded-2xl border-stone-200">
        <div className="grid sm:grid-cols-5 gap-3 items-end">
          <div><Label className="text-xs">De</Label><Input type="date" value={filters.from} onChange={(e) => { setPage(1); setFilters({...filters, from: e.target.value}); }} className="rounded-xl mt-1" data-testid="orders-from"/></div>
          <div><Label className="text-xs">Até</Label><Input type="date" value={filters.to} onChange={(e) => { setPage(1); setFilters({...filters, to: e.target.value}); }} className="rounded-xl mt-1" data-testid="orders-to"/></div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={filters.status} onValueChange={(v) => { setPage(1); setFilters({...filters, status: v}); }}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="orders-status"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="paid">Pago</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="cancelled">Cancelado</SelectItem>
                <SelectItem value="failed">Falhou</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Empresa</Label>
            <Select value={filters.company_id || "all"} onValueChange={(v) => { setPage(1); setFilters({...filters, company_id: v}); }}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="orders-company"><SelectValue placeholder="Todas"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={load} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl">Atualizar</Button>
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4 rounded-2xl border-stone-200"><p className="text-xs text-stone-500">GMV (pago)</p><p className="font-display text-2xl font-bold text-blue-950 mt-1">{fmt(data.totals?.gmv_paid)}</p></Card>
        <Card className="p-4 rounded-2xl border-stone-200"><p className="text-xs text-stone-500">{scope === "admin" ? "Receita Mercado Pago" : "A receber"}</p><p className="font-display text-2xl font-bold text-emerald-700 mt-1">{fmt(scope === "admin" ? data.totals?.mp_volume_paid : data.totals?.to_receive)}</p></Card>
        <Card className="p-4 rounded-2xl border-stone-200"><p className="text-xs text-stone-500">Pedidos pagos</p><p className="font-display text-2xl font-bold mt-1">{data.totals?.count_paid || 0}</p></Card>
        <Card className="p-4 rounded-2xl border-stone-200"><p className="text-xs text-stone-500">Total</p><p className="font-display text-2xl font-bold mt-1">{data.total || 0}</p></Card>
      </div>

      <Card className="rounded-2xl border-stone-200 overflow-hidden">
        {loading ? <div className="p-10 text-center text-stone-400"><Loader2 className="mx-auto animate-spin"/></div> :
         (data.orders || []).length === 0 ? <div className="p-10 text-center text-stone-500"><ShoppingBag className="mx-auto mb-2 text-stone-300" size={32}/>Nenhum pedido no filtro.</div> :
         <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2">Pedido / Data</th>
                {scope === "admin" && <th className="text-left px-4 py-2">Empresa</th>}
                <th className="text-left px-4 py-2">Cliente</th>
                <th className="text-right px-4 py-2">Subtotal</th>
                <th className="text-right px-4 py-2">BemCash</th>
                <th className="text-right px-4 py-2">MP</th>
                <th className="text-right px-4 py-2">{scope === "admin" ? "Taxa" : "A receber"}</th>
                <th className="text-center px-4 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {(data.orders || []).map((o) => (
                <tr key={o.id} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`order-row-${o.id}`}>
                  <td className="px-4 py-2"><span className="font-mono text-xs text-stone-400">#{o.id.slice(0,8)}</span><br/><span className="text-xs">{fmtDate(o.created_at)}</span></td>
                  {scope === "admin" && <td className="px-4 py-2 font-medium">{o.company_name}</td>}
                  <td className="px-4 py-2"><span className="text-stone-700">{o.customer_name || "—"}</span><br/><span className="text-[11px] text-stone-500">{o.customer_phone}</span></td>
                  <td className="text-right px-4 py-2 font-semibold">{fmt(o.subtotal)}</td>
                  <td className="text-right px-4 py-2 text-emerald-700">{fmt(o.cashback_used)}</td>
                  <td className="text-right px-4 py-2">{fmt(o.mp_amount)}</td>
                  <td className="text-right px-4 py-2 text-amber-700">{fmt(scope === "admin" ? o.platform_fee : o.merchant_credit)}</td>
                  <td className="text-center px-4 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${STATUS_COLORS[o.status] || "bg-stone-100 text-stone-600"}`}>{o.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
         </div>}
      </Card>

      {data.pages > 1 && (
        <div className="flex justify-center items-center gap-3 mt-4">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded-lg" data-testid="orders-prev"><ChevronLeft size={14}/></Button>
          <span className="text-sm text-stone-600">Página {data.page} de {data.pages}</span>
          <Button variant="outline" size="sm" disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="rounded-lg" data-testid="orders-next"><ChevronRight size={14}/></Button>
        </div>
      )}
    </div>
  );
}
